import { problem } from "./problem.js";
/**
 * What a reader can *do* about a gate refusal — the act, never the screen.
 *
 * **`publish_product`, not `open_product_editor`.** A remedy names the remedial act and
 * the dashboard decides how to reach it; an action naming a route would put dashboard
 * navigation in a wire contract that machine clients also read, and would go stale the
 * first time a screen moved.
 *
 * `none` is a real member rather than an absent field. Two of the eight gates genuinely
 * have nothing the reader can do — a tombstoned product is terminal, and a GTIN another
 * organisation holds is not resolvable by the person holding the refusal — and §8.1's
 * whole posture is that a refusal states what is true. c29's rule is the one that binds
 * here: **a remedy that does not exist is worse than silence**, so the absence is stated
 * rather than left for a client to infer from a missing key.
 */
export const REMEDY_ACTIONS = [
    "none",
    "wait_for_domain",
    "verify_prefix",
    "await_prefix_review",
    "raise_x_dimension",
    "publish_product",
    "restore_product",
];
/**
 * Which act each gate code offers — **the single list, and the reason code and action
 * cannot drift apart.**
 *
 * `problem.ts`'s header states the hazard this discharges: *"two fields that must agree
 * and are typed independently will disagree."* `code` and `remedy.action` are exactly such
 * a pair — both on the wire, both meaningful to a client — so they are not typed
 * independently. This map is the only place the pairing is written; {@link RemedyFor}
 * looks the action up from the code, and {@link gateRefusal} will not compile if a call
 * site hands `PRODUCT_DRAFT` a `restore_product` remedy.
 *
 * `satisfies Partial<Record<ProblemCode, RemedyAction>>` closes the other direction: a key
 * that is not a real problem code fails to compile, so this census cannot name a gate the
 * registry has never heard of. It is `Partial` rather than total on purpose — eight of
 * thirty-seven codes are gates, and requiring an entry for `UNAUTHENTICATED` would be
 * noise nobody reads.
 *
 * **Two codes map to `none`, and they are not interchangeable.** The action is the same
 * because the *act* is the same — there isn't one — while the `label` and `detail` on each
 * occurrence say two different true things.
 */
export const GATE_REMEDY_ACTIONS = {
    DOMAIN_NEVER_LIVE: "wait_for_domain",
    PREFIX_NOT_VERIFIED: "verify_prefix",
    PREFIX_DISPUTED: "await_prefix_review",
    XDIM_BELOW_MINIMUM: "raise_x_dimension",
    PRODUCT_DRAFT: "publish_product",
    PRODUCT_ARCHIVED: "restore_product",
    PRODUCT_TOMBSTONED: "none",
    GTIN_CLAIMED: "none",
};
/**
 * The eight, as a value — for a caller that needs to enumerate them.
 *
 * `Object.keys` on the map, so the census *is* the map. A hand-written array beside it
 * would be the second list this module exists to avoid, and it is the shape that has gone
 * wrong here before: two of five Phase 0 checks passed while structurally unable to fail,
 * both because a list and the thing it described were maintained separately.
 */
export const GATE_CODES = Object.keys(GATE_REMEDY_ACTIONS);
/**
 * Build one.
 *
 * The generic parameter is what makes the pairing mechanical: `C` is inferred from `code`,
 * and `remedy` is then required to be exactly the arm {@link GATE_REMEDY_ACTIONS} maps that
 * code to. `gateRefusal("PRODUCT_DRAFT", …, { action: "restore_product", … })` does not
 * compile, which is the guarantee this module exists to provide.
 *
 * `status` and `title` come from the registry through `problem()`, so a gate refusal and an
 * ordinary refusal for the same code are identical in every member the two share. There is
 * no second construction path and no place for the two to disagree.
 */
export function gateRefusal(code, detail, remedy) {
    return { ...problem(code, detail), code, remedy };
}
/** Is this body a gate refusal — i.e. does it carry a remedy the UI can render? */
export function isGateRefusal(body) {
    return Object.hasOwn(GATE_REMEDY_ACTIONS, body.code);
}
//# sourceMappingURL=gate.js.map